import React, { useCallback, useContext, useEffect } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Box,
  Button,
  // Link,
  Stack,
  TextField,
  Typography,
  FormHelperText,
  IconButton,
  Checkbox,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { Seo } from "src/components/seo";
import { useNavigate } from "react-router";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useAuth } from "../../../hooks/use-auth";
import { useMounted } from "../../../hooks/use-mounted";
import { usePageView } from "../../../hooks/use-page-view";
import { useSearchParams } from "../../../hooks/use-search-params";
import { paths } from "../../../paths";

import { API_SERVICE_BACKEND, API_SERVICE_BACKEND_2 } from "../../../config";

import axios from "axios";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { getUserInfo } from "src/pages/dashboard/account";
import { resolveSessionPhotoDisplayUrl } from "src/utils/getBlobUrl";
import GoogleIcon from "../../../components/logos/SocialIcon.svg";

import { v4 as uuidv4 } from "uuid";
import { useRouter } from "src/hooks/use-router";
import {
  clearSessionStorage,
  getSessionStorage,
  setLocalStorage,
  setSessionStorage,
} from "src/utils/storage";
import { WhiteLabelConfigurationContext } from "src/contexts/ClientConfiguration";
import { SplashScreen } from "src/components/splash-screen";
import { Link } from "react-router-dom";
import { useGenerateJWTToken, useGetUserAcceptedInvitation } from "src/hooks/react-query/useAuth";
import { NO_HTML_REGEX, noHtmlMessage } from "src/utils/sanitizeInput";
const initialValues = {
  email: "",
  password: "",
  submit: null,
};

export async function generateHMAC(message, key) {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(key);
  const messageData = encoder.encode(message);

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: { name: "SHA-256" } },
    false,
    ["sign"]
  );

  const signature = await crypto.subtle.sign("HMAC", cryptoKey, messageData);
  return Array.from(new Uint8Array(signature))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function setupUserSessionAndIntercom(userInfo, data) {
  const loginProfileImage = userInfo.profile_image;
  setSessionStorage(
    "photoUrl",
    loginProfileImage ? await resolveSessionPhotoDisplayUrl(loginProfileImage) : ""
  );
  setSessionStorage("organization_id", userInfo?.organization_id);
  setSessionStorage("userEmail", data?.email);
  setSessionStorage("companyName", data?.companyName || "No Company Name");
  setSessionStorage("fullName", data?.displayName);
  setSessionStorage(
    "SubscriptionStartDate",
    userInfo?.userSubscription?.subscriptions?.base?.startDate
  );
  setSessionStorage(
    "SubscriptionEndDate",
    userInfo?.userSubscription?.subscriptions?.base?.endDate
  );
  setSessionStorage(
    "SubscriptionType",
    userInfo?.userSubscription?.subscriptions?.base?.planId
  );
  if (userInfo?.userSubscription) {
    setSessionStorage("Paid", true);
  } else {
    setSessionStorage("Paid", false);
  }
  const hash = await generateHMAC(
    getSessionStorage("userEmail"),
    process.env.REACT_APP_INTERCOM_HASH_KEY
  );
  if (window.Intercom) {
    window.Intercom("boot", {
      app_id: "qaecvjwi",
      name: getSessionStorage("fullName"),
      email: getSessionStorage("userEmail"),
      user_hash: hash,
    });
  }
}

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Must be a valid email")
    .max(255)
    .matches(NO_HTML_REGEX, noHtmlMessage("Email"))
    .required("Email is required"),
  password: Yup.string().max(255).required("Password is required"),
});
const Page = () => {
  const navigate = useNavigate();
  const isMounted = useMounted();
  const router = useRouter();
  const {
    issuer,
    updateAuthState,
    signInWithEmailAndPassword,
    signInWithGoogle,
    signOut,
    setUserInfo
  } = useAuth();

  const searchParams = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const theme = useTheme();

  const status =
    searchParams.get("status") || getSessionStorage("magic_link_status");

  const [loading, setloading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const { customEmailConfigDomain, isLogin } = useContext(WhiteLabelConfigurationContext);

  // Snackbar states
  const [Baropen, setBarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("success");
  const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });
  // eslint-disable-next-line no-unused-expressions

  const { mutateAsync: getUserActiveWorkspaces } = useGetUserAcceptedInvitation();
  const { mutateAsync: generateJWTToken } = useGenerateJWTToken();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, helpers) => {
      const rawEmail = values.email ?? formik.values.email ?? "";
      const email = rawEmail.trim().toLowerCase();
      if (!email || email === "") {
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: "Email is required" });
        helpers.setSubmitting(false);
        setloading(false);
        return;
      }

      setloading(true);
      try {
        clearSessionStorage();
        const user = await signInWithEmailAndPassword(email, values.password);
        if (!user) {
          setloading(false);
          return;
        }

        const userEmail = user?.email || email;
        
        if (!userEmail) {
          throw new Error("Unable to retrieve user email");
        }

        const response = await axios.get(
          `${API_SERVICE_BACKEND}/getuserdetail?email=${encodeURIComponent(userEmail)}&saveLogin=true`
        );
        const userData = response.data;
        //console.log({ userData });
        const hash = await generateHMAC(
          userData.data.email,
          process.env.REACT_APP_INTERCOM_HASH_KEY
        );

        if (userData.data.accountStatus === 0) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: "Account Disabled" });
          helpers.setSubmitting(false);
          setloading(false);
        } else {
          if (isMounted()) {
            if (window.Intercom) {
              window.Intercom("boot", {
                app_id: "qaecvjwi",
              });
            }
            window.intercomSettings = {
              app_id: "qaecvjwi",
              name: userData.data.name,
              email: userData.data.email,
              user_hash: hash,
            };
            
            await setupUserSessionAndIntercom(userData.data, {
              email: userData.data.email,
              companyName: userData.data.companyName,
              displayName: userData.data.name
            });

            //console.log(userData, "<<<userData");
            if (userData?.data?.email) {
              const isVIPUser = userData?.data?.isVIPUser;
              if (userData?.data?.signature?.status !== 'completed' &&
  !isVIPUser) {
                setTimeout(() => {
                  navigate(`/signaturePage/${userData?.data?.email}`);
                }, 0);
              }
              else {
                // If user came from a link to purchase/payment, send them there directly
                if (returnTo && (returnTo.includes('/purchase') || returnTo.includes('/checkout'))) {
                  window.location.href = returnTo;
                } else if (
                  !userData?.data?.userSubscription?.subscriptions?.base?.planId
                ) {
                  const userActiveWorkspaces = await getUserActiveWorkspaces(
                    userData.data.email
                  );
                  const workspaces = Array.isArray(userActiveWorkspaces) ? userActiveWorkspaces : [];

                  if (workspaces.length > 0) {
                    window.location.href = returnTo || paths.authDemo.workspace;
                  } else {
                    window.location.href = returnTo || paths.authDemo.pricingPage;
                  }
                } else {
                  window.location.href = returnTo || paths.authDemo.workspace;
                }
              }
            }
            // if (userData?.data?.freeSubscription === true) {
            //   // window.location.href = returnTo || paths.dashboard.campaigns;
            //   setSessionStorage("isNewUser", false)
            //   window.location.href = returnTo || paths.authDemo.workspace;
            // } else if (!userData?.data?.subscribedUsers) {
            //   window.location.href = paths.authDemo.workspace
            //   //window.location.href = paths.authDemo.pricingPage;
            // } else {
            //   // window.location.href = returnTo || paths.dashboard.campaigns;
            //   setSessionStorage("isNewUser", false)
            //   window.location.href = returnTo || paths.authDemo.workspace;
            // }
            setloading(false);
          }
        }
      } catch (err) {
        setloading(false);
        if (isMounted()) {
          helpers.setStatus({ success: false });
          let errorMessage = err.message;
          
          if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
            errorMessage = "Incorrect password";
          } else if (err.code === "auth/user-not-found") {
            errorMessage = "No user found with this email address";
          } else if (err.message?.includes("Incorrect password")) {
            errorMessage = "Incorrect password";
          }
          
          helpers.setErrors({ submit: errorMessage });
          helpers.setSubmitting(false);
        }
      }
    },
  });





  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handleGoogleClick = useCallback(async () => {
    clearSessionStorage();
    try {
      setloading(true);
      const data = await signInWithGoogle();
      if (data) {
        var organization_id = String(`${Date.now()}_${uuidv4()}`);
        var dataSend = {
          name: data?.displayName,
          companyName: data?.companyName || "No Company Name",
          email: data?.email,
          phone: data?.phoneNumber || "",
          organization_id: organization_id,
          plan: "No Plan",
          role: "admin",
          campaginManager: "None",
          campaginManagerEmail: "none@gmail.com",
          accountStatus: 1,
          profile_image: data?.photoURL || null,
        };

        // const resGoogleLog = await fetch1(
        //   `${API_SERVICE_BACKEND}/addregistereduser`,
        //   {
        //     method: "POST",
        //     headers: {
        //       Accept: "application/json",
        //       "Content-Type": "application/json",
        //     },
        //     body: JSON.stringify(dataSend),
        //   }
        // );
        const resGoogleLog = await axios.post(
          `${API_SERVICE_BACKEND}/addregistereduser`,
          dataSend,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );

        const resGoogleLogData = resGoogleLog.data;

        if (resGoogleLog.status === 200 || resGoogleLog.status === 201 || resGoogleLogData.error === "User Already Registered") {
          if (isMounted()) {
            const userInfo = await getUserInfo(data?.email);

            if (!userInfo) {
              openSnackbar("Your account is not active. please contact to administratior.", "error");
              return false;
            }

            if (returnTo && (returnTo.includes('/purchase') || returnTo.includes('/checkout'))) {
              await setupUserSessionAndIntercom(userInfo, data);
              window.location.href = returnTo;
            } else if (!userInfo?.userSubscription?.subscriptions?.base?.planId) {
              const userActiveWorkspaces = await getUserActiveWorkspaces(
                userInfo.email
              );
              const workspaces = Array.isArray(userActiveWorkspaces) ? userActiveWorkspaces : [];

              if (workspaces.length > 0) {
                await setupUserSessionAndIntercom(userInfo, data);
                window.location.href = returnTo || paths.authDemo.workspace;
              } else {
                await setupUserSessionAndIntercom(userInfo, data);
                window.location.href = returnTo || paths.authDemo.pricingPage;
              }
            } else {
              await setupUserSessionAndIntercom(userInfo, data);
              window.location.href = returnTo || paths.authDemo.workspace;
            }
          }
        } else {
          console.error(`[GOOGLE_LOGIN] DB record creation failed for ${data?.email}. Status: ${resGoogleLog.status}. Signing out...`);
          
          try {
            await signOut();
            console.log(`[GOOGLE_LOGIN] Firebase user signed out successfully`);
          } catch (signOutError) {
            console.error(`[GOOGLE_LOGIN] Failed to sign out:`, signOutError);
          }
        }
        await setUserInfo(data?.email);
      }
    } catch (err) {
      console.error(`[GOOGLE_LOGIN] Error during login:`, err);
      try {
        await signOut();
      } catch (signOutError) {
        console.error(`[GOOGLE_LOGIN] Failed to sign out:`, signOutError);
      }
    } finally {
      setloading(false);
    }
  }, [signInWithGoogle, isMounted, returnTo, signOut]);

  const openSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setBarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setBarOpen(false);
  };

  useEffect(() => {
    if (status === 'in-active') {
      openSnackbar("Your account is not active. please contact to administratior.", "error");
      clearSessionStorage();
    }
  }, [status])

  const email = getSessionStorage("userEmail");
  const ownerEmail = getSessionStorage("ownerEmail");

  useEffect(() => {
    if (email && !ownerEmail && !loading) {
      navigate(paths.authDemo.workspace);
    }
  }, [email]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  usePageView();
  const getDomainFromLocation = () => {
    const domain = window.location.hostname;
    return domain;
  };

  const domain = getDomainFromLocation();
  return (
    <>
      <Seo title="Login" />
      <Snackbar
        open={Baropen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {isLogin ? (
        <>
          <SplashScreen />
        </>
      ) : (
        <div>
          <Stack sx={{ mb: 3 }} spacing={1}>
            <Typography
              style={{ fontSize: "32px", fontWeight: "700", color: theme.palette.text.primary }}
            >
              Log In
            </Typography>
            {(domain === "app.b2brocket.ai" || domain === "qa.b2brocket.ai" || domain === "localhost") &&
              <Typography color="text.secondary" variant="body2">
                Don&apos;t have an account? &nbsp;
                <Link
                  to="/auth-main/register/v2"
                  underline="hover"
                  variant="subtitle2"
                  style={{ color: theme.palette.primary.main }}
                  className="glass-link"
                >
                  Register
                </Link>
              </Typography>
            }
          </Stack >
          <form noValidate onSubmit={formik.handleSubmit}>
            <Stack spacing={2}>
              <Stack spacing={1}>
                <Typography style={{ fontSize: "14px", color: theme.palette.text.primary }}>Email</Typography>
                <TextField
                  error={!!(formik.touched.email && formik.errors.email)}
                  variant="outlined"
                  name="email"
                  placeholder="Enter your email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="glass-input"
                />
                {formik.touched.email && formik.errors.email ? (
                  <div
                    style={{
                      color: theme.palette.error.main,
                      marginTop: "0px",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <ErrorOutlineIcon fontSize="14px" />
                    <p style={{ fontSize: "12px", marginLeft: "5px" }}>
                      {formik.errors.email}
                    </p>
                  </div>
                ) : null}
              </Stack>
              <Stack spacing={1}>
                <Typography style={{ fontSize: "14px", color: theme.palette.text.primary }}>Password</Typography>
                <TextField
                  error={!!(formik.touched.password && formik.errors.password)}
                  variant="outlined"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your Password"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.password}
                  className="glass-input"
                />
                {formik.touched.password && formik.errors.password ? (
                  <div
                    style={{
                      color: theme.palette.error.main,
                      marginTop: "0px",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <ErrorOutlineIcon fontSize="14px" />
                    <p style={{ fontSize: "12px", marginLeft: "5px" }}>
                      {formik.errors.password}
                    </p>
                  </div>
                ) : null}
              </Stack>
            </Stack>
            {formik.errors.submit && (
              <FormHelperText error sx={{ mt: 3 }}>
                {formik.errors.submit === "Incorrect password" || 
                 formik.errors.submit === "Firebase: Error (auth/wrong-password)." ||
                 formik.errors.submit === "Firebase: Error (auth/invalid-credential)." ? (
                  <>Incorrect password.</>
                ) : formik.errors.submit === "No user found with this email address" ||
                  formik.errors.submit === "Firebase: Error (auth/user-not-found)." ? (
                  <>No user found with this email address</>
                ) : formik.errors.submit === "Firebase: Error (auth/invalid-email)." ? (
                  <>Invalid email address</>
                ) : formik.errors.submit === "Firebase: Error (auth/too-many-requests)." ? (
                  <>Too many attempts. Please try again later.</>
                ) : formik.errors.submit === "Account Disabled" ? (
                  <>Your account is disabled please contact the support</>
                ) : (
                  <>{formik.errors.submit}</>
                )}
              </FormHelperText>
            )}
            <Box
              sx={{
                mt: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: "14px", color: theme.palette.text.primary }}>
                <Checkbox /> Remember me
              </div>
              <Link
                to="/auth-main/reset-password/v2"
                underline="hover"
                variant="subtitle2"
                style={{ color: theme.palette.primary.main, fontSize: "14px" }}
                // className={(domain === "app.b2brocket.ai" || domain === "localhost") ? "" : "glass-link"}
                className="glass-link"
              >
                Forgot password?
              </Link>
            </Box>
            <Button
              fullWidth
              sx={{ mt: 3 }}
              size="medium"
              type="submit"
              disabled={loading}
              variant="contained"
              style={{ backgroundColor: theme.palette.primary.main, fontSize: "14px" }}
            >
              {loading && (
                <CircularProgress
                  size={16}
                  color="inherit"
                  style={{ marginRight: 8 }}
                />
              )}
              Sign in
            </Button>
            {(domain === "app.b2brocket.ai" || domain === "qa.b2brocket.ai" || domain === "localhost") &&
              <Button
                fullWidth
                type="button"
                disabled={loading}
                variant="outlined"
                style={{
                  marginTop: "15px",
                  textTransform: "none",
                  padding: "0px",
                }}
                onClick={handleGoogleClick}
                className="glass-button-secondary-50"
              >
                <IconButton
                  color="inherit"
                  aria-label="Sign up with Google"
                  component="span"
                >
                  <img src={GoogleIcon} height="22px" />
                </IconButton>
                <Typography style={{ color: theme.palette.text.primary, fontSize: "14px" }}>
                  Sign in with Google
                </Typography>
              </Button>
            }
          </form>
        </div >
      )}
    </>
  );
};
export default Page;
